import type { ItemResponse } from '@finn-no/mobility-item-insertion-shared';
import pulse, { type TrackerData } from '@finn-no/pulse-sdk';
import { getCategoryForAdViewType } from '@m10s/mobility-mapping-utils';
import { getHuckConfig } from '../utils/getHuckConfig.js';

type InsertionMode = 'create' | 'edit' | 'republish';

const trackPageView = (mode: InsertionMode, item: ItemResponse) => {
  const isCreateMode = mode === 'create';
  const isRepublishMode = mode === 'republish';
  pulse.trackPageView({
    type: 'View',
    name: 'Ad insertion - mobility',
    object: {
      type: 'Form',
      name: document.title,
      stepName: isCreateMode || isRepublishMode ? 'Ad input' : 'Ad edit',
      action: `${isCreateMode ? 'Create' : isRepublishMode ? 'Republish' : 'Edit'} ClassifiedAd`,
      items: [
        {
          type: 'ClassifiedAd',
          id: item.id.toString(),
          contentId: `${item.id}`,
          name: item.data.title ?? 'n/a',
          category: getCategoryForAdViewType(item.model),
          ...(typeof item.data.price?.net === 'number' && { price: item.data.price?.net }),
        },
      ],
    },
    vertical: {
      name: 'mobility',
    },
  });
};

const trackEvent = (data: NecessaryTrackerData, item: ItemResponse) => {
  pulse.trackEvent({
    type: data.type ?? 'Click',
    intent: data.intent ?? 'Open',
    name: 'Ad insertion - mobility',
    object: {
      id: undefined as unknown as string,
      type: 'UIElement',
      elementType: 'Button',
      items: [
        {
          type: 'ClassifiedAd',
          id: item.id.toString(),
          contentId: `${item.id}`,
          name: item.data.title ?? 'n/a',
          category: getCategoryForAdViewType(item.model),
          ...(typeof item.data.price?.net === 'number' && { price: item.data.price?.net }),
        },
      ],
      ...data.object,
    },
    target: data.target?.type
      ? {
          id: undefined as unknown as string,
          type: data.target?.type ?? 'ClassifiedAd',
          contentId: `${item.id}`,
          name: item.data.title ?? 'n/a',
          category: getCategoryForAdViewType(item.model),
        }
      : undefined,
    vertical: { name: 'mobility' },
  });
};

const trackClick = (data: NecessaryTrackerData, item: ItemResponse) => {
  trackEvent(
    {
      ...data,
      type: 'Click',
    },
    item,
  );
};

type NecessaryTrackerData = {
  type?: TrackerData['type'];
  target?: { type: 'ClassifiedAd' };
  intent: TrackerData['intent'];
  object?: {
    id?: string;
    action?: string;
    elementType?: string;
    stepName?: string;
  };
};

/**
 * A hook to track events with Pulse.
 * If no item is provided, it will use the item from the Huck config.
 * @param item - The item to track events for. If not provided, the item from the Huck config will be used.
 * @returns An object with methods to track page views, clicks, and events.
 */
export const usePulse = (item?: ItemResponse) => {
  const config = getHuckConfig();
  const resolvedItem = item ?? config.item;

  const mergeWithResolvedItem = (partial?: Partial<ItemResponse>): ItemResponse => {
    if (!partial) return resolvedItem;

    return {
      ...resolvedItem,
      ...partial,
      data: {
        ...resolvedItem.data,
        ...partial.data,
      },
    };
  };

  return {
    /**
     * Tracks a page view event with Pulse.
     * @param item - Optional partial item data to override or extend the resolved item.
     */
    trackPageView: (item?: Partial<ItemResponse>) => trackPageView(config.mode, mergeWithResolvedItem(item)),
    /**
     * Tracks a click event with Pulse.
     * @param data - The tracker data containing event details like intent and object information.
     * @param item - Optional partial item data to override or extend the resolved item.
     */
    trackClick: (data: NecessaryTrackerData, item?: Partial<ItemResponse>) =>
      trackClick(data, mergeWithResolvedItem(item)),
    /**
     * Tracks a custom event with Pulse.
     * @param data - The tracker data containing event details like type, intent, and object information.
     * @param item - Optional partial item data to override or extend the resolved item.
     */
    trackEvent: (data: NecessaryTrackerData, item?: Partial<ItemResponse>) =>
      trackEvent(data, mergeWithResolvedItem(item)),
  };
};
